import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { RestaurantSettlementService } from "../../services";
import { currency, dateTime } from "../../utils/format";

const money = (value) => currency(Number(value || 0));

const PRIMARY_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-900 disabled:cursor-not-allowed disabled:opacity-60";

export const RestaurantDashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setLoading(true);

    try {
      const data = await RestaurantSettlementService.current();
      setSummary(data || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return (
    <div>
      <PageHeader
        title="Restaurant Dashboard"
        subtitle="Restaurant cash received from bar, expenses, and owner payable."
        actions={
          <button
            type="button"
            onClick={loadSummary}
            disabled={loading}
            className={PRIMARY_BUTTON_CLASS}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="To Receive From Bar"
          value={loading ? "…" : money(summary?.toReceiveFromBar)}
          icon="Receipt"
          tone="gold"
        />

        <StatCard
          label="Received From Bar"
          value={loading ? "…" : money(summary?.receivedFromBar)}
          icon="BadgeDollarSign"
          tone="emerald"
        />

        <StatCard
          label="Restaurant Expenses"
          value={loading ? "…" : money(summary?.restaurantExpenses)}
          icon="Receipt"
          tone="rose"
        />

        <StatCard
          label="Owner Payable"
          value={loading ? "…" : money(summary?.ownerPayable)}
          icon="Banknote"
          tone="navy"
        />
      </div>

      <div className="bms-card mt-6 p-5">
        <h3 className="font-display text-lg font-semibold">
          Current Owner Payment Period
        </h3>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Last Owner Payment
            </div>
            <div className="mt-2 font-medium text-slate-900">
              {summary?.lastOwnerPaymentDate
                ? dateTime(summary.lastOwnerPaymentDate)
                : "No previous payment"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Calculated From
            </div>
            <div className="mt-2 font-medium text-slate-900">
              {summary?.periodStart ? dateTime(summary.periodStart) : "-"}
            </div>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Calculated Up To
            </div>
            <div className="mt-2 font-medium text-slate-900">
              {summary?.periodEnd ? dateTime(summary.periodEnd) : "-"}
            </div>
          </div>
        </div>

        {summary?.ownerSettlementAlreadyRequested && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
            Restaurant → Owner payment is already requested for this period.
            Status: {summary.existingOwnerSettlementStatus}
          </div>
        )}
      </div>
    </div>
  );
};